import { Controller } from "@hotwired/stimulus"

export class ApplicationController extends Controller {
  reportError(error, action, tags = {}) {
    if (!window.appsignal) return

    const span = window.appsignal.createSpan((span) => {
      return span
        .setAction(action)
        .setTags(tags)
        .setError(error)
    })
    window.appsignal.send(span)
  }

  // A JSON API call occasionally receives an HTML page instead — some
  // upstream (proxy, CDN error page, challenge interstitial, a redirect to
  // sign-in) answered in place of Rails. The bare "Expected a JSON response"
  // error identifies nothing, so attach what came back to the error before
  // rethrowing for the caller's reporter (spread `error.responseDiagnostics`
  // into the reportError tags).
  //
  // Every value stays under 96 characters: AppSignal drops any tag value
  // that reaches 100 characters, without warning — a longer snippet would
  // vanish at ingestion.
  async parseJsonResponse(response) {
    try {
      return await response.json
    } catch (error) {
      const body = await this._readBodyOrBlank(response)
      error.responseDiagnostics = {
        status: response.statusCode,
        content_type: response.contentType,
        redirected: response.redirected,
        // Rule break (guarding a required value): FetchResponse always sets
        // `response`, but a TypeError raised in here would replace the very
        // error being reported.
        final_url: (response.response?.url || "").slice(0, 96),
        body_title: this._htmlTitleOf(body),
        body_snippet: body.slice(0, 96)
      }
      throw error
    }
  }

  async _readBodyOrBlank(response) {
    try {
      return String(await response.text)
    } catch {
      // Body unreadable (aborted or already-consumed stream) — the remaining
      // diagnostics still get reported.
      return ""
    }
  }

  // An error page's <title> names its author — "502 Bad Gateway",
  // "Just a moment…", "Sign in · LEANSpark" — where the opening markup is
  // identical boilerplate on every page ever served.
  _htmlTitleOf(body) {
    const title = body.match(/<title[^>]*>([^<]*)/i)
    return (title?.[1] || "").trim().slice(0, 96)
  }

  async handleSessionExpiry(response) {
    if (response.statusCode !== 422) return false

    try {
      const data = await response.json
      if (data?.error === "session_expired") {
        this.reportError(
          new Error("Session expired: CSRF token verification failed"),
          `${this.identifier}.sessionExpired`,
          { path: window.location.pathname }
        )
        window.location.reload()
        return true
      }
    } catch {
      // non-JSON error response
    }

    return false
  }

  showToast(message, type = "info") {
    const container = document.getElementById("notifications")
    if (!container) return

    const toast = document.createElement("div")
    toast.className = `alert alert-${type === "error" ? "error" : "success"} shadow-lg mb-2`
    toast.setAttribute("data-controller", "alert")
    toast.setAttribute("data-alert-dismiss-after-value", "5000")
    const span = document.createElement("span")
    span.textContent = message
    toast.appendChild(span)
    container.appendChild(toast)
  }
}
